import { Helix2000Character } from '../../shared/CharacterTypes';
import { EventBus, EVENTS } from '../../shared/EventBus';
import { ServiceRegistry } from '../core/ServiceRegistry';
import { NetworkManager } from '../network/NetworkManager';
import type { EntitySystem } from '../systems/entity/EntitySystem';
import { logMultiplayerDiagnostic } from './multiplayerDiagnosticLogger';

import { PhaseTimingTracker } from './PhaseTimingTracker';
import { bootSearchParams } from '../../config/boot/bootUrl';

const SAMPLE_INTERVAL_MS = 250;
const RENDER_POSITION_OFFSET = 20;
const MISSING_SPRITE_GRACE_MS = 1000;

// Explicit session opt-in for the multiplayer diagnostic labels:
//   - dev console set window.forceDebug = true
//   - URL contains ?debug=1 (used by `make practice` and explicit debug URLs)
//
// 2026-09-28 (U4): `window.playwrightTest` is deliberately NOT part of this
// path. Playwright boots many clients, and a default E2E run must show exactly
// what a normal player sees — no debug labels. Labels are opt-in everywhere:
// this seed, the persisted UserPrefs.dev.showMultiplayerLabels preference
// (default OFF), or the ?diag=1/?diagAll=1 force-on; see the resolver below
// for the exact precedence.
export function hasDebugLabelOptIn(): boolean {
  if (typeof window === 'undefined') return false;
  if ((window as any).forceDebug) return true;
  try {
    return bootSearchParams().get('debug') === '1';
  } catch {
    return false;
  }
}
const DEBUG_LABEL_SESSION_OPT_IN = hasDebugLabelOptIn();

/**
 * ?diag=1 / ?diagAll=1 — kitchen-sink diagnostic launch forces the labels on
 * regardless of preset override or persisted preference. (Moved here from the
 * retired UIContext, 2026-06-12.)
 */
export function isDiagForcedUrl(): boolean {
  try {
    const params = bootSearchParams();
    return params.get('diag') === '1' || params.get('diagAll') === '1';
  } catch {
    return false;
  }
}

/**
 * Initial label visibility. Truth table (2026-09-28, U4 — labels are opt-in;
 * every surface, including Playwright, defaults OFF):
 *
 *   1. ?diag=1 / ?diagAll=1 force ON over everything, including a preset
 *      `multiplayerLabelsOverride: false`.
 *   2. A non-null preset `multiplayerLabelsOverride` wins next: it suppresses
 *      both the persisted preference and the ?debug=1 / forceDebug session
 *      opt-in (clean/readable presets stay clean).
 *   3. Otherwise labels are ON only when the persisted
 *      UserPrefs.dev.showMultiplayerLabels preference is true OR the explicit
 *      ?debug=1 / window.forceDebug session opt-in is set.
 *   4. Default: OFF (Playwright included — window.playwrightTest enables
 *      nothing).
 *
 * The runtime's module-level seed is the session opt-in only (see
 * hasDebugLabelOptIn). The game-view MultiplayerLabelsReporter re-resolves
 * with this function at mount and on every prefs notification, so the
 * persisted preference / profile override is always the applied authority.
 */
export function resolveInitialMultiplayerLabelsEnabled(
  override: boolean | null | undefined,
  prefEnabled: boolean,
  diagForced: boolean,
  debugOptIn: boolean = false,
): boolean {
  if (diagForced) return true;
  if (override !== null && override !== undefined) return override;
  return prefEnabled || debugOptIn;
}

export interface RemoteMultiplayerDiagnostics {
  entityId: string;
  sessionId: string;
  sessionSuffix: string;
  name: string | null;
  className: string | null;
  level: number | null;
  health: number | null;
  maxHealth: number | null;
  mana: number | null;
  maxMana: number | null;
  isOnline: boolean | null;
  schemaX: number | null;
  schemaY: number | null;
  renderX: number | null;
  renderY: number | null;
  deltaX: number | null;
  deltaY: number | null;
  grounded: boolean | null;
  spriteExists: boolean;
  missingSpriteGraceElapsed: boolean;
  velocityY: number | null;
}

export interface MultiplayerEventCounters {
  playerTeleported: number;
  remoteJoin: number;
  remoteLeave: number;
  reconnect: number;
}

export interface MultiplayerDiagnosticsSnapshot {
  capturedAt: string;
  ready: boolean;
  local: {
    entityId: string | null;
    sessionId: string | null;
    sessionSuffix: string | null;
    clientInstanceId: string | null;
    roomId: string | null;
    roomInstanceId: string | null;
    roomCreatedAt: string | null;
    firstRoomId: string | null;
    firstRoomInstanceId: string | null;
    firstRoomCreatedAt: string | null;
    reconnectCount: number;
    reconnectAttempts: number;
    reconnectStartedAt: string | null;
    reconnectSucceededAt: string | null;
    reconnectFailedAt: string | null;
    disconnectReason: string | null;
    roomChangedSinceJoin: boolean;
    roomInstanceChangedSinceJoin: boolean;
    roomCreatedAtChangedSinceJoin: boolean;
    resetDetected: boolean;
    schemaX: number | null;
    schemaY: number | null;
    renderX: number | null;
    renderY: number | null;
    grounded: boolean | null;
    velocityX: number | null;
    velocityY: number | null;
  };
  remoteSchemaCount: number;
  remoteManagerCount: number;
  remoteRenderCount: number;
  remotes: RemoteMultiplayerDiagnostics[];
  mobs: MobDiagnostics[];
  eventCounters: MultiplayerEventCounters;

  // Phase-1 latency-attribution data — populated from NetworkManager.clockSync.
  // `ready: false` until the first pong/clock-sync sample has been recorded.
  // Phases 3/4/5 of the latency arc add their own snapshot blocks separately
  // (intentionally not collapsed — distinct concerns; see SSOT-qualifier).
  clockSync: {
    ready: boolean;
    /** Median offset across recent samples (server time = client time + offset). */
    offsetMs: number;
    /** Most recent RTT (not median — RTT spikes are signal). */
    rttMs: number;
    /** Approximate one-way latency = rtt/2. */
    oneWayLatencyMs: number;
    /** Number of samples in the rolling buffer. */
    sampleCount: number;
    /** Age of the freshest sample. Infinity before any sample. */
    freshnessMs: number;
  };

  // Phase 3 of the latency-attribution arc — client-side per-frame phase
  // timings (input handler / predict+render / reconcile). Populated from
  // PhaseTimingTracker. `latest` is the most recent frame's sample; the
  // rolling fields cover the last 60 frames (~1 s at 60 fps).
  // Phase-3-4-5 of the conceptual 7-phase budget (network + server tick)
  // are intentionally absent here — they belong to the Phase-2 wire-format
  // change deferred in REMAINING.md.
  phaseTimings: {
    sampleCount: number;
    latest: {
      /** ms from raw browser keydown to next-frame command read; null if no input this frame. */
      phase1Ms: number | null;
      /** ms wall time inside the EntitySystem accumulator + sprite sync. */
      phase2Ms: number;
    } | null;
    rollingMean: { phase1Ms: number; phase2Ms: number };
    rollingMax: { phase1Ms: number; phase2Ms: number };
  };
}

// Snapshot of one mob/NPC for the diagnostic overlay. Mirrors the player
// label fields so the same mental model applies — S = server schema,
// C = client predicted (Mob entity, after physics tick), V = velocity,
// G = grounded, plus HP / aiState which are mob-specific.
export interface MobDiagnostics {
  entityId: string;       // Mob.id (matches server NPC.id)
  type: string;
  name: string | null;
  schemaX: number | null;
  schemaY: number | null;
  renderX: number | null;
  renderY: number | null;
  velocityX: number | null;
  velocityY: number | null;
  grounded: boolean | null;
  health: number | null;
  maxHealth: number | null;
  aiState: string | null;
  spriteExists: boolean;
}

export interface MultiplayerLabelSnapshot {
  entityId: string;
  text: string;
}

export interface MultiplayerDiagnosticsState {
  snapshot: MultiplayerDiagnosticsSnapshot;
  otherPlayers: Map<string, Helix2000Character>;
  labelsEnabled: boolean;
}

interface RemoteManagerDiagnostic {
  sessionId: string;
  entityId: string;
  name?: string;
  className?: string;
  level?: number;
  health?: number;
  maxHealth?: number;
  mana?: number;
  maxMana?: number;
  isOnline?: boolean;
  schemaX?: number;
  schemaY?: number;
  renderX?: number | null;
  renderY?: number | null;
  grounded?: boolean;
  spriteExists?: boolean;
}

type MultiplayerDiagnosticsListener = (state: MultiplayerDiagnosticsState) => void;

function firstNonEmptyString(...values: unknown[]): string | null {
  for (const value of values) {
    if (typeof value === 'string' && value.trim().length > 0) {
      return value;
    }
  }
  return null;
}

function toFiniteNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function formatCoordPair(x: number | null, y: number | null): string {
  if (x === null || y === null) {
    return 'n/a';
  }
  return `(${Math.round(x)}, ${Math.round(y)})`;
}

function formatBooleanLabel(value: boolean | null): string {
  if (value === null) {
    return '?';
  }
  return value ? 'Y' : 'N';
}

function suffix(value: string | null | undefined, size: number = 6): string {
  if (!value) {
    return 'n/a';
  }
  return value.slice(-size);
}

function emptyCounters(): MultiplayerEventCounters {
  return {
    playerTeleported: 0,
    remoteJoin: 0,
    remoteLeave: 0,
    reconnect: 0,
  };
}

function emptySnapshot(): MultiplayerDiagnosticsSnapshot {
  return {
    capturedAt: new Date(0).toISOString(),
    ready: false,
    local: {
      entityId: null,
      sessionId: null,
      sessionSuffix: null,
      clientInstanceId: null,
      roomId: null,
      roomInstanceId: null,
      roomCreatedAt: null,
      firstRoomId: null,
      firstRoomInstanceId: null,
      firstRoomCreatedAt: null,
      reconnectCount: 0,
      reconnectAttempts: 0,
      reconnectStartedAt: null,
      reconnectSucceededAt: null,
      reconnectFailedAt: null,
      disconnectReason: null,
      roomChangedSinceJoin: false,
      roomInstanceChangedSinceJoin: false,
      roomCreatedAtChangedSinceJoin: false,
      resetDetected: false,
      schemaX: null,
      schemaY: null,
      renderX: null,
      renderY: null,
      grounded: null,
      velocityX: null,
      velocityY: null,
    },
    remoteSchemaCount: 0,
    remoteManagerCount: 0,
    remoteRenderCount: 0,
    remotes: [],
    mobs: [],
    eventCounters: emptyCounters(),
    clockSync: emptyClockSync(),
    phaseTimings: emptyPhaseTimings(),
  };
}

function emptyClockSync(): MultiplayerDiagnosticsSnapshot['clockSync'] {
  return {
    ready: false,
    offsetMs: 0,
    rttMs: 0,
    oneWayLatencyMs: 0,
    sampleCount: 0,
    freshnessMs: Infinity,
  };
}

function emptyPhaseTimings(): MultiplayerDiagnosticsSnapshot['phaseTimings'] {
  return {
    sampleCount: 0,
    latest: null,
    rollingMean: { phase1Ms: 0, phase2Ms: 0 },
    rollingMax: { phase1Ms: 0, phase2Ms: 0 },
  };
}

export function normalizeRenderCoordinate(spriteAxis: number | null | undefined, offset: number = RENDER_POSITION_OFFSET): number | null {
  const numeric = toFiniteNumber(spriteAxis);
  return numeric === null ? null : numeric - offset;
}

export function calculateRenderDelta(schemaAxis: number | null | undefined, renderAxis: number | null | undefined): number | null {
  const schema = toFiniteNumber(schemaAxis);
  const render = toFiniteNumber(renderAxis);
  if (schema === null || render === null) {
    return null;
  }
  return render - schema;
}

export function isMissingSpriteGraceElapsed(firstSeenAt: number | null | undefined, nowMs: number, graceMs: number = MISSING_SPRITE_GRACE_MS): boolean {
  if (typeof firstSeenAt !== 'number' || !Number.isFinite(firstSeenAt)) {
    return false;
  }
  return (nowMs - firstSeenAt) >= graceMs;
}

export function inferResetDetected(input: {
  roomChangedSinceJoin: boolean;
  roomInstanceChangedSinceJoin: boolean;
  roomCreatedAtChangedSinceJoin: boolean;
}): boolean {
  return input.roomChangedSinceJoin || input.roomInstanceChangedSinceJoin || input.roomCreatedAtChangedSinceJoin;
}

// Local player label format. Field semantics:
//   S = Server schema position (room.state.players[sessionId].x/y) — authoritative
//   C = Client predicted position (PlayerManager.player.position.x/y) — what the
//       sprite is being drawn from after client-side prediction + reconciliation
//   V = Velocity (vx, vy) from the client-side physics state. When stationary,
//       V should be (0, 0) and S should equal C — any divergence at rest is a
//       reconciliation bug worth investigating
//   G = Grounded (Y/N/?), RI = last 4 chars of room instance id
function buildLocalLabel(snapshot: MultiplayerDiagnosticsSnapshot): MultiplayerLabelSnapshot | null {
  if (!snapshot.local.entityId) {
    return null;
  }

  return {
    entityId: snapshot.local.entityId,
    text: [
      `LOCAL ${suffix(snapshot.local.sessionId)}`,
      `S ${formatCoordPair(snapshot.local.schemaX, snapshot.local.schemaY)}`,
      `C ${formatCoordPair(snapshot.local.renderX, snapshot.local.renderY)}`,
      `V ${formatCoordPair(snapshot.local.velocityX, snapshot.local.velocityY)}`,
      `G ${formatBooleanLabel(snapshot.local.grounded)} RI ${suffix(snapshot.local.roomInstanceId, 4)}`,
    ].join('\n'),
  };
}

// Mob label format. Same S/C/V/G fields as the player so the mental model
// transfers, plus HP and aiState which are mob-specific. Used by MobRenderer
// when areLabelsEnabled() is true.
export function buildMobLabel(mob: MobDiagnostics): MultiplayerLabelSnapshot {
  const hp = mob.health !== null && mob.maxHealth !== null
    ? `${Math.round(mob.health)}/${Math.round(mob.maxHealth)}`
    : 'n/a';
  const stateLabel = mob.aiState ? mob.aiState.toUpperCase() : '?';
  const header = mob.name ? `${mob.type} ${suffix(mob.entityId, 4)}` : `mob ${suffix(mob.entityId, 4)}`;
  return {
    entityId: mob.entityId,
    text: [
      header,
      `S ${formatCoordPair(mob.schemaX, mob.schemaY)}`,
      `C ${formatCoordPair(mob.renderX, mob.renderY)}`,
      `V ${formatCoordPair(mob.velocityX, mob.velocityY)}`,
      `G ${formatBooleanLabel(mob.grounded)} HP ${hp}`,
      `AI ${stateLabel}`,
    ].join('\n'),
  };
}

// Remote player label format. Same semantics as local minus velocity (we don't
// track remote velocity client-side). D = render-vs-schema delta to spot when a
// remote is mid-interpolation.
function buildRemoteLabel(remote: RemoteMultiplayerDiagnostics): MultiplayerLabelSnapshot {
  return {
    entityId: remote.entityId,
    text: [
      `${remote.name || 'Remote'} ${remote.sessionSuffix}`,
      `S ${formatCoordPair(remote.schemaX, remote.schemaY)}`,
      `C ${formatCoordPair(remote.renderX, remote.renderY)}`,
      `G ${formatBooleanLabel(remote.grounded)} D ${formatCoordPair(remote.deltaX, remote.deltaY)}`,
    ].join('\n'),
  };
}

class MultiplayerDiagnosticsRuntime {
  private listeners = new Set<MultiplayerDiagnosticsListener>();
  private eventCounters = emptyCounters();
  private otherPlayers = new Map<string, Helix2000Character>();
  private labelsEnabled = DEBUG_LABEL_SESSION_OPT_IN;
  private snapshot: MultiplayerDiagnosticsSnapshot = emptySnapshot();
  private labelData = new Map<string, MultiplayerLabelSnapshot>();
  private remoteFirstSeenAt = new Map<string, number>();
  private lastRoomMeta: Record<string, unknown> | null = null;
  private lastServerDiagnostics: Record<string, unknown> | null = null;
  private started = false;
  private unsubscribers: Array<() => void> = [];

  public ensureStarted(): void {
    if (this.started || typeof window === 'undefined') {
      return;
    }

    this.started = true;
    const eventBus = EventBus.getInstance();
    this.unsubscribers.push(
      eventBus.on('net:message:playerTeleported', () => {
        this.eventCounters.playerTeleported++;
        this.sample();
      }),
      eventBus.on('net:remotePlayerJoin', () => {
        this.eventCounters.remoteJoin++;
        this.sample();
      }),
      eventBus.on('net:remotePlayerLeave', () => {
        this.eventCounters.remoteLeave++;
        this.sample();
      }),
      eventBus.on(EVENTS.NETWORK_RECONNECTING, () => {
        this.eventCounters.reconnect++;
        this.sample();
      }),
      eventBus.on('net:connected', () => this.sample()),
      eventBus.on('net:disconnected', () => this.sample()),
      eventBus.on('net:roomMeta', (payload: Record<string, unknown>) => {
        this.lastRoomMeta = payload;
        this.sample();
      }),
      eventBus.on('net:message:networkDiagnostics', (payload: Record<string, unknown>) => {
        this.lastServerDiagnostics = payload;
        this.sample();
      }),
    );

    setInterval(() => this.sample(), SAMPLE_INTERVAL_MS);
    this.sample();
  }

  public subscribe(listener: MultiplayerDiagnosticsListener): () => void {
    this.ensureStarted();
    this.listeners.add(listener);
    listener(this.getState());
    return () => {
      this.listeners.delete(listener);
    };
  }

  public getState(): MultiplayerDiagnosticsState {
    this.ensureStarted();
    return {
      snapshot: this.snapshot,
      // K15: hand out the runtime's Map directly (treat as read-only) —
      // cloning here defeated the stable-identity guarantee sample() keeps.
      otherPlayers: this.otherPlayers,
      labelsEnabled: this.labelsEnabled,
    };
  }

  public getSnapshot(): MultiplayerDiagnosticsSnapshot {
    this.ensureStarted();
    return this.snapshot;
  }

  public getLabel(entityId: string): MultiplayerLabelSnapshot | null {
    this.ensureStarted();
    return this.labelData.get(entityId) || null;
  }

  public areLabelsEnabled(): boolean {
    this.ensureStarted();
    return this.labelsEnabled;
  }

  public setLabelsEnabled(enabled: boolean): void {
    this.ensureStarted();
    if (this.labelsEnabled === enabled) {
      return;
    }
    this.labelsEnabled = enabled;
    this.publish();
  }

  private publish(): void {
    if (typeof window !== 'undefined') {
      (window as any).__HELIX_MP_DIAGNOSTICS__ = this.snapshot;
    }
    const state = this.getState();
    this.listeners.forEach((listener) => listener(state));
  }

  private sample(): void {
    const registry = ServiceRegistry.getInstance();
    const networkManager = registry.get('NetworkManager') || (window as any).nm as NetworkManager | undefined;
    const entitySystem = registry.get('EntitySystem')
      || (window as any).helix?.kernel?.getSystem?.('EntitySystem') as EntitySystem | undefined;

    const diagnostics = networkManager?.getDiagnostics?.() as Record<string, any> | undefined;
    const room = networkManager?.getRoom?.();
    const roomData = networkManager?.getRoomData?.();
    const roomPlayers = room?.state?.players;
    const localSessionId = typeof room?.sessionId === 'string' ? room.sessionId : null;
    const playerManager = entitySystem?.getPlayerManager?.() || null;
    const remotePlayerManager = entitySystem?.getRemotePlayerManager?.() || null;
    const localPlayer = playerManager?.getPlayer?.() || null;
    const localSprite = playerManager?.getPlayerSprite?.() || null;
    const localSchema = localSessionId && roomPlayers?.get ? roomPlayers.get(localSessionId) : null;

    const localEntityId = localPlayer?.id || null;
    const localRenderX = normalizeRenderCoordinate(toFiniteNumber((localSprite as any)?.x));
    const localRenderY = normalizeRenderCoordinate(toFiniteNumber((localSprite as any)?.y));

    const remoteDiagnosticsRaw = (remotePlayerManager?.getDiagnostics?.() || []) as RemoteManagerDiagnostic[];
    const remoteDiagnosticsById = new Map(remoteDiagnosticsRaw.map((entry) => [entry.sessionId, entry]));
    const remotePlayers = remotePlayerManager?.getOtherPlayers?.() || new Map<string, Helix2000Character>();
    // K15: keep the Map identity STABLE unless the roster actually changed.
    // A fresh Map per 250ms sample made every consumer effect keyed on
    // `otherPlayers` re-fire per publish (Game.tsx setOtherPlayers), which
    // scheduled an update during every passive-effect flush — React's
    // nested-passive-update counter (limit 50) then tripped "Maximum update
    // depth exceeded" whenever sustained patch traffic (buff ticks, dashes,
    // brawl bots) kept commits gapless for ~2s. Identity only changes here
    // when a session joins/leaves or a character object is replaced.
    const rosterChanged = remotePlayers.size !== this.otherPlayers.size
      || [...remotePlayers].some(([id, ch]) => this.otherPlayers.get(id) !== ch);
    if (rosterChanged) {
      this.otherPlayers = new Map(remotePlayers);
    }

    const remoteSchemaIds = new Set<string>();
    if (roomPlayers?.forEach) {
      roomPlayers.forEach((_player: any, sessionId: string) => {
        if (sessionId !== localSessionId) {
          remoteSchemaIds.add(sessionId);
        }
      });
    }

    const nowMs = Date.now();
    const allRemoteIds = new Set<string>([
      ...Array.from(remoteSchemaIds),
      ...Array.from(remotePlayers.keys()),
      ...Array.from(remoteDiagnosticsById.keys()),
    ]);

    const nextRemoteFirstSeenAt = new Map<string, number>();
    const remotes: RemoteMultiplayerDiagnostics[] = Array.from(allRemoteIds)
      .sort((a, b) => a.localeCompare(b))
      .map((sessionId) => {
        const schema = roomPlayers?.get ? roomPlayers.get(sessionId) : null;
        const remoteDiagnostic = remoteDiagnosticsById.get(sessionId);
        const remotePlayer = remotePlayers.get(sessionId);
        const firstSeenAt = this.remoteFirstSeenAt.get(sessionId) || nowMs;
        nextRemoteFirstSeenAt.set(sessionId, firstSeenAt);

        const schemaX = toFiniteNumber(schema?.x ?? remoteDiagnostic?.schemaX ?? remotePlayer?.position?.x);
        const schemaY = toFiniteNumber(schema?.y ?? remoteDiagnostic?.schemaY ?? remotePlayer?.position?.y);
        const renderX = toFiniteNumber(remoteDiagnostic?.renderX);
        const renderY = toFiniteNumber(remoteDiagnostic?.renderY);
        const spriteExists = Boolean(remoteDiagnostic?.spriteExists);

        return {
          entityId: remoteDiagnostic?.entityId || sessionId,
          sessionId,
          sessionSuffix: suffix(sessionId, 6),
          name: schema?.heroName || remotePlayer?.name || remoteDiagnostic?.name || null,
          className: schema?.heroClass || remotePlayer?.class || remoteDiagnostic?.className || null,
          level: toFiniteNumber(schema?.level ?? remotePlayer?.level ?? remoteDiagnostic?.level),
          health: toFiniteNumber(schema?.health ?? remotePlayer?.health ?? remoteDiagnostic?.health),
          maxHealth: toFiniteNumber(schema?.maxHealth ?? remotePlayer?.maxHealth ?? remoteDiagnostic?.maxHealth),
          mana: toFiniteNumber(schema?.mana ?? remotePlayer?.mana ?? remoteDiagnostic?.mana),
          maxMana: toFiniteNumber(schema?.maxMana ?? remotePlayer?.maxMana ?? remoteDiagnostic?.maxMana),
          isOnline: typeof schema?.isOnline === 'boolean'
            ? schema.isOnline
            : typeof remotePlayer?.isOnline === 'boolean'
              ? remotePlayer.isOnline
              : typeof remoteDiagnostic?.isOnline === 'boolean'
                ? remoteDiagnostic.isOnline
                : null,
          schemaX,
          schemaY,
          renderX,
          renderY,
          deltaX: calculateRenderDelta(schemaX, renderX),
          deltaY: calculateRenderDelta(schemaY, renderY),
          grounded: typeof schema?.isGrounded === 'boolean'
            ? schema.isGrounded
            : typeof remotePlayer?.isGrounded === 'boolean'
              ? remotePlayer.isGrounded
              : typeof remoteDiagnostic?.grounded === 'boolean'
                ? remoteDiagnostic.grounded
                : null,
          spriteExists,
          missingSpriteGraceElapsed: !spriteExists && isMissingSpriteGraceElapsed(firstSeenAt, nowMs),
          velocityY: toFiniteNumber(remotePlayer?.velocity?.y),
        };
      });
    this.remoteFirstSeenAt = nextRemoteFirstSeenAt;

    const previousSnapshot = this.snapshot;
    const resolvedRoomId = firstNonEmptyString(
      diagnostics?.roomId,
      diagnostics?.roomMeta?.roomId,
      this.lastRoomMeta?.roomId,
      diagnostics?.serverDiagnostics?.room_id,
      this.lastServerDiagnostics?.room_id,
      (room as any)?.roomId,
      (room as any)?.id,
      roomData?.roomId,
    );
    const resolvedRoomInstanceId = firstNonEmptyString(
      diagnostics?.roomInstanceId,
      diagnostics?.roomMeta?.roomInstanceId,
      this.lastRoomMeta?.roomInstanceId,
      diagnostics?.serverDiagnostics?.room_instance_id,
      this.lastServerDiagnostics?.room_instance_id,
      roomData?.roomInstanceId,
    );
    const resolvedRoomCreatedAt = firstNonEmptyString(
      diagnostics?.roomCreatedAt,
      diagnostics?.serverDiagnostics?.room_created_at,
      this.lastServerDiagnostics?.room_created_at,
    );
    const localSchemaY = toFiniteNumber(localSchema?.y ?? localPlayer?.position?.y);

    // Sample mobs (NPCs) — joins server schema (room.state.npcs) with the
    // client-side Mob entity instances from MobManager. Server provides the
    // authoritative schema position; client Mob holds the post-physics-tick
    // C-position used for rendering.
    const mobsData: MobDiagnostics[] = [];
    try {
      const mobSystem = registry.get('MobSystem', { optional: true });
      const mobManager = mobSystem?.getManager?.();
      const clientMobs = (mobManager?.getMobsMap?.() as Map<string, any> | undefined) || new Map();
      const serverNpcs = (room?.state as any)?.npcs;

      // Iterate server NPCs (authoritative source); cross-reference with client mob.
      //
      // IMPORTANT coordinate space: server schema `npc.x/y` is the TOP-LEFT
      // corner of the hitbox (the same convention used in map files). The
      // client `mob.x/y` is the CENTER (Phaser sprite anchor), recomputed each
      // frame in RemoteMobManager.update() as `mob.position.x + mob.width/2`.
      // To compare apples-to-apples we must read the client's `mob.position`
      // (top-left, kept in sync with the schema by RemoteMobManager) — NOT
      // `mob.x/y` (center). Reading the wrong field made S and C look like
      // they had a 90-ish px desync when they were actually identical.
      if (serverNpcs?.forEach) {
        serverNpcs.forEach((npc: any, npcId: string) => {
          const clientMob = clientMobs.get(npcId);
          const clientPosition = clientMob?.position;
          mobsData.push({
            entityId: npcId,
            type: typeof npc?.type === 'string' ? npc.type : 'unknown',
            name: typeof npc?.name === 'string' ? npc.name : null,
            schemaX: toFiniteNumber(npc?.x),
            schemaY: toFiniteNumber(npc?.y),
            renderX: toFiniteNumber(clientPosition?.x),
            renderY: toFiniteNumber(clientPosition?.y),
            velocityX: toFiniteNumber(clientMob?.velocity?.x ?? npc?.velocityX),
            velocityY: toFiniteNumber(clientMob?.velocity?.y ?? npc?.velocityY),
            grounded: typeof clientMob?.isGrounded === 'boolean'
              ? clientMob.isGrounded
              : typeof npc?.isGrounded === 'boolean'
                ? npc.isGrounded
                : null,
            health: toFiniteNumber(npc?.health),
            maxHealth: toFiniteNumber(npc?.maxHealth),
            aiState: typeof npc?.aiState === 'string' ? npc.aiState : null,
            spriteExists: Boolean(clientMob),
          });
        });
      }
    } catch (err) {
      // Mob sampling is best-effort — never crash the diagnostic loop on a
      // missing manager or schema mismatch.
      logMultiplayerDiagnostic('warn', 'mob_sample_failed', { error: String(err) });
    }

    const nextSnapshot: MultiplayerDiagnosticsSnapshot = {
      capturedAt: new Date(nowMs).toISOString(),
      ready: Boolean(networkManager && entitySystem && localPlayer),
      local: {
        entityId: localEntityId,
        sessionId: localSessionId,
        sessionSuffix: localSessionId ? suffix(localSessionId, 6) : null,
        clientInstanceId: typeof diagnostics?.clientInstanceId === 'string' ? diagnostics.clientInstanceId : null,
        roomId: resolvedRoomId,
        roomInstanceId: resolvedRoomInstanceId,
        roomCreatedAt: resolvedRoomCreatedAt,
        firstRoomId: typeof diagnostics?.firstRoomId === 'string' ? diagnostics.firstRoomId : null,
        firstRoomInstanceId: typeof diagnostics?.firstRoomInstanceId === 'string' ? diagnostics.firstRoomInstanceId : null,
        firstRoomCreatedAt: typeof diagnostics?.firstRoomCreatedAt === 'string' ? diagnostics.firstRoomCreatedAt : null,
        reconnectCount: toFiniteNumber(diagnostics?.reconnectCount) || 0,
        reconnectAttempts: toFiniteNumber(diagnostics?.reconnectAttempts) || 0,
        reconnectStartedAt: typeof diagnostics?.lastReconnectStartedAt === 'string' ? diagnostics.lastReconnectStartedAt : null,
        reconnectSucceededAt: typeof diagnostics?.lastReconnectSucceededAt === 'string' ? diagnostics.lastReconnectSucceededAt : null,
        reconnectFailedAt: typeof diagnostics?.lastReconnectFailedAt === 'string' ? diagnostics.lastReconnectFailedAt : null,
        disconnectReason: typeof diagnostics?.lastDisconnectReason === 'string' ? diagnostics.lastDisconnectReason : null,
        roomChangedSinceJoin: Boolean(diagnostics?.roomChangedSinceJoin),
        roomInstanceChangedSinceJoin: Boolean(diagnostics?.roomInstanceChangedSinceJoin),
        roomCreatedAtChangedSinceJoin: Boolean(diagnostics?.roomCreatedAtChangedSinceJoin),
        resetDetected: inferResetDetected({
          roomChangedSinceJoin: Boolean(diagnostics?.roomChangedSinceJoin),
          roomInstanceChangedSinceJoin: Boolean(diagnostics?.roomInstanceChangedSinceJoin),
          roomCreatedAtChangedSinceJoin: Boolean(diagnostics?.roomCreatedAtChangedSinceJoin),
        }),
        schemaX: toFiniteNumber(localSchema?.x ?? localPlayer?.position?.x),
        schemaY: localSchemaY,
        renderX: localRenderX,
        renderY: localRenderY,
        grounded: typeof localSchema?.isGrounded === 'boolean'
          ? localSchema.isGrounded
          : typeof localPlayer?.isGrounded === 'boolean'
            ? localPlayer.isGrounded
            : null,
        velocityX: toFiniteNumber(localPlayer?.velocity?.x),
        velocityY: toFiniteNumber(localPlayer?.velocity?.y),
      },
      remoteSchemaCount: remoteSchemaIds.size,
      remoteManagerCount: remotePlayers.size,
      remoteRenderCount: remotes.filter((remote) => remote.spriteExists).length,
      remotes,
      mobs: mobsData,
      eventCounters: { ...this.eventCounters },
      clockSync: (() => {
        // NetworkManager exposes clockSync as a public-readonly field. Same
        // resolution path as the existing networkManager lookup above —
        // ServiceRegistry first, window.nm fallback (the boot-order defence
        // pattern from PR #64). When neither is present, ship empty values
        // so the Latency tab can render "n/a" cleanly.
        const cs = (networkManager as any)?.clockSync;
        if (!cs) return emptyClockSync();
        return {
          ready: cs.isReady(),
          offsetMs: cs.offset(),
          rttMs: cs.rtt(),
          oneWayLatencyMs: cs.oneWayLatency(),
          sampleCount: cs.sampleCount(),
          freshnessMs: cs.freshnessMs(),
        };
      })(),
      phaseTimings: PhaseTimingTracker.size() === 0 ? emptyPhaseTimings() : {
        sampleCount: PhaseTimingTracker.size(),
        latest: PhaseTimingTracker.latest(),
        rollingMean: {
          phase1Ms: PhaseTimingTracker.rollingMean('phase1Ms'),
          phase2Ms: PhaseTimingTracker.rollingMean('phase2Ms'),
        },
        rollingMax: {
          phase1Ms: PhaseTimingTracker.rollingMax('phase1Ms'),
          phase2Ms: PhaseTimingTracker.rollingMax('phase2Ms'),
        },
      },
    };

    this.snapshot = nextSnapshot;

    this.labelData.clear();
    const localLabel = buildLocalLabel(nextSnapshot);
    if (localLabel) {
      this.labelData.set(localLabel.entityId, localLabel);
    }
    nextSnapshot.remotes.forEach((remote) => {
      this.labelData.set(remote.entityId, buildRemoteLabel(remote));
    });
    nextSnapshot.mobs.forEach((mob) => {
      this.labelData.set(mob.entityId, buildMobLabel(mob));
    });

    if (typeof window !== 'undefined') {
      (window as any).__HELIX_MP_DIAGNOSTICS__ = nextSnapshot;
    }

    if (nextSnapshot.local.resetDetected && !previousSnapshot.local.resetDetected) {
      logMultiplayerDiagnostic('warn', 'client_reset_detected', {
        roomId: nextSnapshot.local.roomId,
        roomInstanceId: nextSnapshot.local.roomInstanceId,
        firstRoomId: nextSnapshot.local.firstRoomId,
        firstRoomInstanceId: nextSnapshot.local.firstRoomInstanceId,
      });
    }

    this.publish();
  }
}

function createRuntime(): MultiplayerDiagnosticsRuntime {
  return new MultiplayerDiagnosticsRuntime();
}

const runtime = (() => {
  const globalScope = globalThis as any;
  if (!globalScope.__HELIX_MP_DIAGNOSTICS_RUNTIME__) {
    globalScope.__HELIX_MP_DIAGNOSTICS_RUNTIME__ = createRuntime();
  }
  return globalScope.__HELIX_MP_DIAGNOSTICS_RUNTIME__ as MultiplayerDiagnosticsRuntime;
})();

export function getMultiplayerDiagnosticsRuntime(): MultiplayerDiagnosticsRuntime {
  return runtime;
}
